/**
 * EmailConfirmationPendingPage - Shown after registration
 * SESSION_189: Email confirmation enforcement
 *
 * Displays:
 * - Message to check email
 * - User's email address
 * - "Try Again" button to check if email is confirmed
 * - Redirect to dashboard if email is confirmed
 */
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Title,
  Text,
  Button,
  Stack,
  Alert,
  Center,
  ThemeIcon,
  Group,
  Loader
} from '@mantine/core';
import { IconMail, IconRefresh, IconCheck, IconAlertCircle, IconSend } from '@tabler/icons-react';
import { useAuthStore, updateEmailConfirmedStatus } from '../../../application/store/authStore';
import { apiClient } from '../../../infrastructure/api/ApiClient';
import { API_ENDPOINTS } from '../../../infrastructure/api/config';
import { CUSTOMER_ROUTES, NONPROFIT_ROUTES } from '../../routing/routes';

interface EmailStatusResponse {
  emailConfirmed: boolean;
  email: string;
}

export const EmailConfirmationPendingPage: React.FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [isChecking, setIsChecking] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [justConfirmed, setJustConfirmed] = useState(false);

  const handleTryAgain = useCallback(async (): Promise<void> => {
    setIsChecking(true);
    setError(null);

    try {
      const response = await apiClient.get<EmailStatusResponse>('/api/auth/email-status');

      if (response.emailConfirmed) {
        // Update the auth store
        updateEmailConfirmedStatus(true);
        setJustConfirmed(true);

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          const dashboard = user?.role === 'NonProfitUser'
            ? NONPROFIT_ROUTES.DASHBOARD
            : CUSTOMER_ROUTES.DASHBOARD;
          navigate(dashboard, { replace: true });
        }, 2000);
      } else {
        setError('Email not yet confirmed. Please check your inbox and click the confirmation link.');
      }
    } catch {
      setError('Unable to check email status. Please try again.');
    } finally {
      setIsChecking(false);
    }
  }, [navigate]);

  const handleResend = useCallback(async (): Promise<void> => {
    const email = user?.email;
    if (!email) return;

    setIsResending(true);
    setError(null);
    setResendSuccess(false);

    try {
      await apiClient.post(API_ENDPOINTS.AUTH.RESEND_EMAIL_CONFIRMATION, { email });
      setResendSuccess(true);
    } catch {
      setError('Unable to resend confirmation email. Please try again later.');
    } finally {
      setIsResending(false);
    }
  }, [user?.email]);

  // If email was just confirmed, show success message
  if (justConfirmed) {
    return (
      <Container size="sm" py="xl">
        <Paper shadow="md" p="xl" radius="md" withBorder>
          <Stack align="center" gap="lg">
            <ThemeIcon size={80} radius="xl" color="green" variant="light">
              <IconCheck size={48} />
            </ThemeIcon>

            <Title order={2} ta="center">
              Email Confirmed!
            </Title>

            <Text c="dimmed" ta="center">
              Your email has been confirmed. Redirecting to your dashboard...
            </Text>

            <Loader size="md" />
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container size="sm" py="xl">
      <Paper shadow="md" p="xl" radius="md" withBorder>
        <Stack align="center" gap="lg">
          <ThemeIcon size={80} radius="xl" color="blue" variant="light">
            <IconMail size={48} />
          </ThemeIcon>

          <Title order={2} ta="center">
            Check Your Email
          </Title>

          <Text c="dimmed" ta="center" maw={400}>
            We&apos;ve sent a confirmation email to:
          </Text>

          <Text fw={600} size="lg" ta="center">
            {user?.email ?? 'your email address'}
          </Text>

          <Text c="dimmed" ta="center" maw={400}>
            Please click the confirmation link in the email to verify your account
            and access all features.
          </Text>

          {error && (
            <Alert
              icon={<IconAlertCircle size={16} />}
              color="red"
              variant="light"
              w="100%"
            >
              {error}
            </Alert>
          )}

          {resendSuccess && (
            <Alert
              icon={<IconCheck size={16} />}
              color="green"
              variant="light"
              w="100%"
            >
              A new confirmation email has been sent. Please check your inbox.
            </Alert>
          )}

          <Group gap="md">
            <Button
              leftSection={<IconRefresh size={18} />}
              onClick={() => void handleTryAgain()}
              loading={isChecking}
              size="md"
            >
              {isChecking ? 'Checking...' : 'I\'ve Confirmed My Email'}
            </Button>
          </Group>

          <Center>
            <Text size="sm" c="dimmed">
              Didn&apos;t receive the email?{' '}
              <Button
                variant="subtle"
                size="compact-sm"
                leftSection={<IconSend size={14} />}
                onClick={() => void handleResend()}
                loading={isResending}
                disabled={resendSuccess}
              >
                {resendSuccess ? 'Email sent' : 'Resend confirmation email'}
              </Button>
            </Text>
          </Center>
        </Stack>
      </Paper>
    </Container>
  );
};

export default EmailConfirmationPendingPage;
